import { type JSX, useCallback, useEffect, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import { liste as kundenliste } from '../api/kunden';
import { alsTag } from '../datum';
import {
  type Artikelzeile,
  type Rabattkondition,
  type Warengruppe,
  konditionAnlegen,
  konditionBeenden,
  konditionen as holeKonditionen,
  liste as artikelliste,
  rabattgruppen as holeRabattgruppen,
  warengruppen as holeWarengruppen,
} from '../api/produkte';
import { Dialog } from '../komponenten/Dialog';
import { Meldung } from '../komponenten/Meldung';
import { Liste } from '../komponenten/Liste';
import { useZaehler, zaehlwort } from '../komponenten/Seitenkontext';
import { alsEuro } from '../betrag';

/**
 * Rabattkonditionen — Stufe 3 der Preisfindung (PRD-15, Tabelle 22, SYS-10).
 *
 * ============================================================================
 * WARUM DIESE MASKE ENTSTANDEN IST
 * ============================================================================
 *
 * Der Rechenkern führt eine **Stufe 3** und liest sie aus
 * `fach.rabattkondition`. Die Endpunkte gab es; eine Maske nicht — und damit
 * lief die Stufe seit ihrem Bau nachweislich immer leer. Am 09.09.2026 stand
 * **null** Konditionen im Bestand.
 *
 * ============================================================================
 * ES GIBT KEIN ÄNDERN, UND DAS IST ABSICHT
 * ============================================================================
 *
 * PRF-16: „Eine Preis- **oder Konditionsänderung** wirkt nie rückwirkend auf
 * bestehende Angebote, Aufträge, Buchungen oder Belege." Wer eine laufende
 * Kondition überschriebe, änderte rückwirkend, was in der Preisherleitung
 * eines schon geschriebenen Angebots steht (PRF-13).
 *
 * Der Weg ist deshalb **beenden und neu anlegen** — dieselbe Bauart wie bei
 * den Preiszeilen. Die alte Kondition bleibt stehen und bleibt auffindbar;
 * sie hört nur auf zu gelten.
 *
 * ============================================================================
 * ZWEI ENTWEDER-ODER, DIE VERSCHIEDENES BEDEUTEN
 * ============================================================================
 *
 *   **Wer** sie bekommt: genau eines von Kunde und Rabattgruppe. Keines
 *   gälte für niemanden, beides für zwei verschiedene Mengen.
 *
 *   **Worauf** sie wirkt: höchstens eines von Artikel und Warengruppe.
 *   *Keines von beiden heißt „auf alles"* — das ist der Regelfall eines
 *   allgemeinen Kundenrabatts und keine fehlende Angabe.
 *
 * Die Maske schaltet deshalb um, statt Felder nebeneinanderzustellen: Zwei
 * gefüllte Träger wären ein Formular, das die API abweist, und das sähe man
 * ihm nicht an.
 *
 * ============================================================================
 * SEITENTYP LISTE (ÄA-41)
 * ============================================================================
 *
 * Zähler im Seitenkopf, „Kondition anlegen" in der Leiste, die Konditionen
 * als Tabelle in einer Karte. Angelegt wird weiter im Fenster: Das Formular
 * schaltet zwischen Trägern und Bereichen um und sucht Kunden — zu viel für
 * eine Seitenleiste.
 */
export function Rabattkonditionen({
  darfKonfigurieren,
}: {
  readonly darfKonfigurieren: boolean;
}): JSX.Element {
  const [zeilen, setzeZeilen] = useState<readonly Rabattkondition[]>([]);
  const [gruppen, setzeGruppen] = useState<readonly string[]>([]);
  const [warengruppen, setzeWarengruppen] = useState<readonly Warengruppe[]>([]);
  const [artikel, setzeArtikel] = useState<readonly Artikelzeile[]>([]);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [meldung, setzeMeldung] = useState('');
  const [neu, setzeNeu] = useState(false);
  const [formular, setzeFormular] = useState<Formular>(leer());
  const [laeuft, setzeLaeuft] = useState(false);
  const [kundensuche, setzeKundensuche] = useState('');
  const [treffer, setzeTreffer] = useState<readonly { id: string; text: string }[]>([]);

  const laden = useCallback(async (): Promise<void> => {
    setzeLaedt(true);
    try {
      const [k, r, w, a] = await Promise.all([
        holeKonditionen(),
        holeRabattgruppen(),
        holeWarengruppen(),
        artikelliste({}),
      ]);
      setzeZeilen(k);
      setzeGruppen(r);
      setzeWarengruppen(w);
      setzeArtikel(a.artikel);
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaedt(false);
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  async function kundenSuchen(): Promise<void> {
    if (kundensuche.trim().length < 3) {
      setzeTreffer([]);
      return;
    }
    try {
      const ergebnis = await kundenliste({ suche: kundensuche.trim() });
      setzeTreffer(
        ergebnis.kunden.slice(0, 10).map((k) => ({
          id: k.id,
          text: `${k.kundennummer} · ${k.name}`,
        })),
      );
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    }
  }

  async function absenden(ereignis: React.FormEvent): Promise<void> {
    ereignis.preventDefault();
    setzeLaeuft(true);
    setzeFehler(undefined);
    try {
      await konditionAnlegen({
        kennung: formular.kennung.trim(),
        bezeichnung: formular.bezeichnung.trim(),
        kunde: formular.traeger === 'kunde' ? nullWennLeer(formular.kunde) : null,
        rabattgruppe:
          formular.traeger === 'rabattgruppe' ? nullWennLeer(formular.rabattgruppe) : null,
        artikel: formular.bereich === 'artikel' ? nullWennLeer(formular.artikel) : null,
        warengruppe:
          formular.bereich === 'warengruppe' ? nullWennLeer(formular.warengruppe) : null,
        art: formular.art,
        prozent: formular.art === 'prozent' ? nullWennLeer(formular.prozent) : null,
        betrag: formular.art === 'betrag' ? nullWennLeer(formular.betrag) : null,
        nichtKombinierbar: formular.nichtKombinierbar,
        gueltigAb: formular.gueltigAb,
        gueltigBis: nullWennLeer(formular.gueltigBis),
      });
      setzeMeldung(`Kondition ${formular.kennung.trim()} angelegt.`);
      setzeNeu(false);
      setzeFormular(leer());
      await laden();
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaeuft(false);
    }
  }

  async function beenden(kondition: Rabattkondition): Promise<void> {
    setzeMeldung('');
    setzeFehler(undefined);
    try {
      await konditionBeenden(kondition.id, heute());
      setzeMeldung(`Kondition ${kondition.kennung} endet heute.`);
      await laden();
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    }
  }

  const jetzt = heute();

  /* ÄA-41: der Zähler im Seitenkopf — erst, wenn geladen ist. */
  useZaehler(laedt ? undefined : zaehlwort(zeilen.length, 'Kondition', 'Konditionen'));

  return (
    <>
      {fehler !== undefined && <Meldung fehler={fehler} />}

      <Liste
        {...(darfKonfigurieren
          ? {
              filter: (
                <button
                  className="knopf knopf--haupt"
                  type="button"
                  onClick={() => {
                    setzeMeldung('');
                    setzeFehler(undefined);
                    setzeFormular(leer());
                    setzeNeu(true);
                  }}
                >
                  Kondition anlegen
                </button>
              ),
            }
          : {})}
        hinweise={
          <>
            {/* PRF-16 */}
            <p className="hilfe">
              Stufe 3 der Preisfindung (Tabelle 22). Sie wirkt nach Preisliste und Staffel und
              vor Mitgliedschaft und Aktionscode. Geändert wird nicht — eine laufende Kondition
              wird beendet und neu angelegt, sonst änderte sich rückwirkend, was in einer schon
              geschriebenen Preisherleitung steht.
            </p>
            {meldung !== '' && (
              <p className="hinweiszeile" role="status">
                {meldung}
              </p>
            )}
          </>
        }
      >
        {laedt ? (
          <p role="status">Wird geladen …</p>
        ) : zeilen.length === 0 ? (
          <div className="liste__leer">
            <p className="hinweiszeile">
              Es ist keine Kondition gepflegt. Die Stufe 3 der Preisfindung läuft damit leer:
              Jeder Kundenrabatt müsste an der Position von Hand eingetragen werden.
            </p>
          </div>
        ) : (
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle" aria-label="Rabattkonditionen">
              <thead>
                <tr>
                  <th scope="col">Kennung</th>
                  <th scope="col">Bezeichnung</th>
                  <th scope="col">Gilt für</th>
                  <th scope="col">Wirkt auf</th>
                  <th scope="col">Nachlass</th>
                  <th scope="col">Zeitraum</th>
                  {darfKonfigurieren && <th scope="col">Handlung</th>}
                </tr>
              </thead>
              <tbody>
                {zeilen.map((k) => {
                  const abgelaufen = k.gueltigBis !== null && k.gueltigBis < jetzt;
                  return (
                    <tr key={k.id} className={abgelaufen ? 'liste__zeile--blass' : undefined}>
                      <th scope="row" className="liste__nummer">
                        <code>{k.kennung}</code>
                      </th>
                      <td>
                        {k.bezeichnung}
                        {/*
                          PRF-08: „nicht kombinierbar" verdrängt die übrigen
                          Vorteile derselben und der folgenden Stufen. Das ist eine
                          Wertung und trägt deshalb eine Marke.
                        */}
                        {k.nichtKombinierbar && (
                          <span className="marke marke--warn"> nicht kombinierbar</span>
                        )}
                      </td>
                      <td>
                        {k.rabattgruppe ??
                          `${k.kundenname ?? ''}${k.kundenname === null ? 'unbekannter Kunde' : ''}`}
                      </td>
                      <td>
                        {/*
                          KEIN BEREICH HEISST „AUF ALLES" und ist der Regelfall
                          eines allgemeinen Kundenrabatts — kein fehlender Wert.
                        */}
                        {k.artikelname ?? k.warengruppenname ?? (
                          <span className="hilfe">alle Artikel</span>
                        )}
                      </td>
                      <td className="zahl">
                        {k.art === 'prozent' ? `${k.prozent ?? ''} %` : alsEuro(k.betrag)}
                      </td>
                      <td>
                        <span className="liste__einzeilig">ab {alsTag(k.gueltigAb)}</span>
                        {k.gueltigBis === null ? (
                          <span className="hilfe"> · unbefristet</span>
                        ) : (
                          <>
                            {' '}
                            <span className="liste__einzeilig">bis {alsTag(k.gueltigBis)}</span>
                          </>
                        )}
                      </td>
                      {darfKonfigurieren && (
                        <td>
                          <button
                            className="knopf knopf--klein"
                            type="button"
                            disabled={k.gueltigBis !== null && k.gueltigBis <= jetzt}
                            onClick={() => void beenden(k)}
                          >
                            Heute beenden
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Liste>

      {neu && (
        <Dialog titel="Neue Kondition" breite="breit" beiSchliessen={() => setzeNeu(false)}>
          <form onSubmit={(e) => void absenden(e)}>
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="rkKennung">
                Kennung <span className="feld__pflicht">Pflicht</span>
              </label>
              <input
                id="rkKennung"
                className="feld__eingabe"
                value={formular.kennung}
                onChange={(e) => {
                  setzeFormular({ ...formular, kennung: e.target.value.toUpperCase() });
                }}
                placeholder="GROSSKUNDE-5"
              />
              <p className="feld__hilfe">
                Großbuchstaben, Ziffern und Bindestrich, 2 bis 30 Zeichen. Unter ihr
                erscheint die Kondition in der Preisherleitung.
              </p>
            </div>

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="rkBezeichnung">
                Bezeichnung <span className="feld__pflicht">Pflicht</span>
              </label>
              <input
                id="rkBezeichnung"
                className="feld__eingabe"
                value={formular.bezeichnung}
                onChange={(e) => {
                  setzeFormular({ ...formular, bezeichnung: e.target.value });
                }}
              />
            </div>

            <fieldset>
              <legend>Wer sie bekommt</legend>
              <div className="feld">
                <label htmlFor="rkTraegerGruppe">
                  <input
                    id="rkTraegerGruppe"
                    type="radio"
                    name="traeger"
                    checked={formular.traeger === 'rabattgruppe'}
                    onChange={() => setzeFormular({ ...formular, traeger: 'rabattgruppe' })}
                  />{' '}
                  Eine Rabattgruppe
                </label>{' '}
                <label htmlFor="rkTraegerKunde">
                  <input
                    id="rkTraegerKunde"
                    type="radio"
                    name="traeger"
                    checked={formular.traeger === 'kunde'}
                    onChange={() => setzeFormular({ ...formular, traeger: 'kunde' })}
                  />{' '}
                  Ein einzelner Kunde
                </label>
                <p className="feld__hilfe">
                  Genau eines von beidem — Tabelle 22 nennt Stufe 3 „Kundenkondition
                  (Rabattgruppe oder Kundenrabatt)".
                </p>
              </div>

              {formular.traeger === 'rabattgruppe' ? (
                <div className="feld">
                  <label className="feld__beschriftung" htmlFor="rkGruppe">
                    Rabattgruppe
                  </label>
                  <select
                    id="rkGruppe"
                    className="feld__eingabe"
                    value={formular.rabattgruppe}
                    onChange={(e) => {
                      setzeFormular({ ...formular, rabattgruppe: e.target.value });
                    }}
                  >
                    <option value="">— wählen —</option>
                    {gruppen.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                  {/* KUN-10 */}
                  <p className="feld__hilfe">
                    Aus den tatsächlich am Kunden vergebenen Gruppen.
                  </p>
                </div>
              ) : (
                <div className="feld">
                  <label className="feld__beschriftung" htmlFor="rkKundensuche">
                    Kunde suchen
                  </label>
                  <input
                    id="rkKundensuche"
                    className="feld__eingabe"
                    value={kundensuche}
                    onChange={(e) => {
                      setzeKundensuche(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void kundenSuchen();
                      }
                    }}
                  />
                  <button className="knopf" type="button" onClick={() => void kundenSuchen()}>
                    Suchen
                  </button>
                  <ul className="rechteliste">
                    {treffer.map((t) => (
                      <li key={t.id}>
                        <button
                          className="knopf knopf--klein"
                          type="button"
                          aria-pressed={formular.kunde === t.id}
                          onClick={() => setzeFormular({ ...formular, kunde: t.id })}
                        >
                          {t.text}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </fieldset>

            <fieldset>
              <legend>Worauf sie wirkt</legend>
              <div className="feld">
                <label className="feld__beschriftung" htmlFor="rkBereich">
                  Geltungsbereich
                </label>
                <select
                  id="rkBereich"
                  className="feld__eingabe"
                  value={formular.bereich}
                  onChange={(e) => {
                    setzeFormular({ ...formular, bereich: e.target.value as Bereich });
                  }}
                >
                  <option value="alle">Alle Artikel</option>
                  <option value="warengruppe">Eine Warengruppe</option>
                  <option value="artikel">Ein einzelner Artikel</option>
                </select>
                <p className="feld__hilfe">
                  „Alle Artikel" ist der Regelfall eines allgemeinen Kundenrabatts und keine
                  fehlende Angabe.
                </p>
              </div>

              {formular.bereich === 'warengruppe' && (
                <div className="feld">
                  <label className="feld__beschriftung" htmlFor="rkWarengruppe">
                    Warengruppe
                  </label>
                  <select
                    id="rkWarengruppe"
                    className="feld__eingabe"
                    value={formular.warengruppe}
                    onChange={(e) => {
                      setzeFormular({ ...formular, warengruppe: e.target.value });
                    }}
                  >
                    <option value="">— wählen —</option>
                    {warengruppen.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.bezeichnung}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formular.bereich === 'artikel' && (
                <div className="feld">
                  <label className="feld__beschriftung" htmlFor="rkArtikel">
                    Artikel
                  </label>
                  <select
                    id="rkArtikel"
                    className="feld__eingabe"
                    value={formular.artikel}
                    onChange={(e) => {
                      setzeFormular({ ...formular, artikel: e.target.value });
                    }}
                  >
                    <option value="">— wählen —</option>
                    {artikel.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.artikelnummer} · {a.bezeichnung}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </fieldset>

            <fieldset>
              <legend>Der Nachlass</legend>
              <div className="feld">
                <label className="feld__beschriftung" htmlFor="rkArt">
                  Art
                </label>
                <select
                  id="rkArt"
                  className="feld__eingabe"
                  value={formular.art}
                  onChange={(e) => {
                    setzeFormular({ ...formular, art: e.target.value as 'prozent' | 'betrag' });
                  }}
                >
                  <option value="prozent">Prozentual</option>
                  <option value="betrag">Absoluter Betrag</option>
                </select>
              </div>

              {formular.art === 'prozent' ? (
                <div className="feld">
                  <label className="feld__beschriftung" htmlFor="rkProzent">
                    Prozentsatz
                  </label>
                  <input
                    id="rkProzent"
                    className="feld__eingabe"
                    value={formular.prozent}
                    onChange={(e) => {
                      setzeFormular({ ...formular, prozent: e.target.value });
                    }}
                    placeholder="5.00"
                    inputMode="decimal"
                  />
                </div>
              ) : (
                <div className="feld">
                  <label className="feld__beschriftung" htmlFor="rkBetrag">
                    Betrag
                  </label>
                  <input
                    id="rkBetrag"
                    className="feld__eingabe"
                    value={formular.betrag}
                    onChange={(e) => {
                      setzeFormular({ ...formular, betrag: e.target.value });
                    }}
                    placeholder="25.00"
                    inputMode="decimal"
                  />
                </div>
              )}

              <div className="feld">
                <label className="feld__beschriftung" htmlFor="rkNichtKombinierbar">
                  <input
                    id="rkNichtKombinierbar"
                    type="checkbox"
                    checked={formular.nichtKombinierbar}
                    onChange={(e) => {
                      setzeFormular({ ...formular, nichtKombinierbar: e.target.checked });
                    }}
                  />{' '}
                  Nicht kombinierbar
                </label>
                {/* PRF-08 */}
                <p className="feld__hilfe">
                  Sie verdrängt dann die übrigen Vorteile derselben und der folgenden Stufen.
                </p>
              </div>
            </fieldset>

            <div className="feldreihe feldreihe--halb">
              <div className="feld">
                <label className="feld__beschriftung" htmlFor="rkAb">
                  Gültig ab <span className="feld__pflicht">Pflicht</span>
                </label>
                <input
                  id="rkAb"
                  type="date"
                  className="feld__eingabe"
                  value={formular.gueltigAb}
                  onChange={(e) => {
                    setzeFormular({ ...formular, gueltigAb: e.target.value });
                  }}
                />
                <p className="feld__hilfe">
                  Frühestens heute. Eine Konditionsänderung wirkt nie rückwirkend.
                </p>
              </div>
              <div className="feld">
                <label className="feld__beschriftung" htmlFor="rkBis">
                  Gültig bis
                </label>
                <input
                  id="rkBis"
                  type="date"
                  className="feld__eingabe"
                  value={formular.gueltigBis}
                  onChange={(e) => {
                    setzeFormular({ ...formular, gueltigBis: e.target.value });
                  }}
                />
              </div>
            </div>

            <div className="feldreihe">
              <button className="knopf knopf--haupt" type="submit" disabled={laeuft}>
                Anlegen
              </button>{' '}
              <button
                className="knopf knopf--verweis"
                type="button"
                onClick={() => setzeNeu(false)}
              >
                Abbrechen
              </button>
            </div>
          </form>
        </Dialog>
      )}
    </>
  );
}

type Bereich = 'alle' | 'artikel' | 'warengruppe';

type Formular = {
  kennung: string;
  bezeichnung: string;
  traeger: 'kunde' | 'rabattgruppe';
  kunde: string;
  rabattgruppe: string;
  bereich: Bereich;
  artikel: string;
  warengruppe: string;
  art: 'prozent' | 'betrag';
  prozent: string;
  betrag: string;
  nichtKombinierbar: boolean;
  gueltigAb: string;
  gueltigBis: string;
};

/** Ortszeit (FRS-01) — `toISOString()` wäre UTC und abends der Vortag. */
function heute(): string {
  const d = new Date();
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

function leer(): Formular {
  return {
    kennung: '',
    bezeichnung: '',
    traeger: 'rabattgruppe',
    kunde: '',
    rabattgruppe: '',
    bereich: 'alle',
    artikel: '',
    warengruppe: '',
    art: 'prozent',
    prozent: '',
    betrag: '',
    nichtKombinierbar: false,
    gueltigAb: heute(),
    gueltigBis: '',
  };
}

function nullWennLeer(wert: string): string | null {
  const geputzt = wert.trim();
  return geputzt === '' ? null : geputzt;
}
